import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  sessionAssignationContextKey,
  sessionAssignationStore,
  useSessionAssignationStore,
} from "@context/sessionAssignation";
import { AppContext, InitialStateType } from "../../context/EditSessionsContext";
import { resultReducer, ResultActions, Types as ResultTypes } from "../../context/result.reducer";
import { buildSessionAssignationResults } from "../../utils/session-assignation/results";

/** Keeps one validated presentation alive until post-save confirmation finishes. */
export const useSessionAssignationResultsContext = (onReload: () => Promise<boolean>) => {
  const parent = useContext(AppContext);
  const draft = useSessionAssignationStore(
    useShallow(state => ({
      context: state.context,
      generation: state.generation,
      phase: state.phase,
      validated: state.validated,
      issues: state.issues,
    })),
  );
  const [snapshot] = useState(() => {
    const current = sessionAssignationStore.getState();
    if (!current.context || !current.validated || !current.isCurrent(current.validated.token))
      return null;
    return {
      contextKey: sessionAssignationContextKey(current.context),
      token: current.validated.token,
      presentation: buildSessionAssignationResults(parent.state, current, current.validated),
    };
  });
  const [presentation, setPresentation] = useState<InitialStateType | null>(
    snapshot?.presentation ?? null,
  );
  const active = !!(
    snapshot &&
    draft.context &&
    draft.generation === snapshot.token.generation &&
    sessionAssignationContextKey(draft.context) === snapshot.contextKey &&
    ["results", "committing", "saved"].includes(draft.phase)
  );

  useEffect(() => {
    if (snapshot) return;
    const current = sessionAssignationStore.getState();
    if (current.phase === "results") current.backToEditing();
    else if (current.phase === "saved") void onReload();
  }, [snapshot, draft.phase, onReload]);

  const parentDispatch = parent.dispatch;
  const dispatch = useCallback(
    (action: Parameters<typeof parentDispatch>[0]) => {
      const current = sessionAssignationStore.getState();
      if (
        !snapshot ||
        !current.context ||
        !current.isCurrent(snapshot.token) ||
        sessionAssignationContextKey(current.context) !== snapshot.contextKey
      )
        return;
      if (
        action &&
        "type" in action &&
        Object.values(ResultTypes).includes(action.type as ResultTypes)
      )
        setPresentation(value => ({
          ...value,
          result: resultReducer(value.result, action as ResultActions),
        }));
      else parentDispatch(action);
    },
    [parentDispatch, snapshot],
  );

  const context = useMemo(
    () =>
      active && presentation
        ? {
            state: {
              ...presentation,
              page: { ...presentation.page, loading: parent.state.page.loading },
            },
            dispatch,
          }
        : null,
    [active, presentation, parent.state.page.loading, dispatch],
  );

  return { context, phase: draft.phase, validated: draft.validated, issues: draft.issues };
};
